<script lang="ts">
    
    import { onDestroy } from "svelte";

    export let src: string;

    let audio: HTMLAudioElement;
    let timeControlElement: HTMLElement;
    let progress = 0;
    let isControlCaptured = false;
    let wasPlaying: boolean;
    let isPlaying = false;

    onDestroy(onControlRelease);

    function onTogglePlay(): void {
        if (!audio) {
            return;
        }
        if (audio.paused) {
            audio.play();
        } else {
            audio.pause();
        }
    }

    function onTimeupdate(): void {
        if (isControlCaptured) {
            return;
        }
        progress = audio.currentTime / audio.duration;
    }

    function onControlCapture(event: MouseEvent | TouchEvent): void {
        if (isControlCaptured) {
            return;
        }
        isControlCaptured = true;
        event.preventDefault();
        wasPlaying = !audio.paused;
        audio.pause();
        window.addEventListener('mousemove', onControlMove);
        window.addEventListener('touchmove', onControlMove);
        window.addEventListener('mouseup', onControlRelease);
        window.addEventListener('touchend', onControlRelease);
    }

    function onControlMove(event: MouseEvent | TouchEvent): void {
        event.preventDefault();
        setProgressByEvent(event);
    }

    function onControlRelease(): void {
        isControlCaptured = false;
        window.removeEventListener('mousemove', onControlMove);
        window.removeEventListener('touchmove', onControlMove);
        window.removeEventListener('mouseup', onControlRelease);
        window.removeEventListener('touchend', onControlRelease);
        audio.currentTime = audio.duration * progress;
        if (wasPlaying) {
            audio.play();
        }
    }

    function onTimeControlClick(event: MouseEvent | TouchEvent): void {
        event.preventDefault();
        setProgressByEvent(event);
        audio.currentTime = audio.duration * progress;
    }

    function setProgressByEvent(event: MouseEvent | TouchEvent): void {
        const clientX = getEventClientX(event);
        const { left, width } = timeControlElement.getBoundingClientRect();
        progress = Math.max(0, Math.min(1, (clientX - left) / width));
    }

    function getEventClientX(event: MouseEvent | TouchEvent): number {
        if (event instanceof TouchEvent) {
            const { clientX } = event.touches.item(0);
            return clientX;
        }
        return event.clientX;
    }

    function onPlay(): void {
        isPlaying = true;
    }

    function onPause(): void {
        isPlaying = false;
    }

</script>

<audio
    {src}
    bind:this={audio}
    on:timeupdate={onTimeupdate}
    on:play={onPlay}
    on:pause={onPause}></audio>

<div class="audio-player">
    <button type="button" class="play-btn" class:is-playing={isPlaying} on:click={onTogglePlay}></button>
    <div class="time-control" bind:this={timeControlElement} on:click={onTimeControlClick}>
        <div class="control-bar" style="width: {100 * progress}%;">
            <div class="control-handle" on:mousedown={onControlCapture} on:touchstart={onControlCapture}></div>
        </div>
    </div>
</div>

<style lang="scss">

    .audio-player {
        --time-control-height: 6px;

        background-color: var(--color-primary);
        padding: 1rem;
        display: flex;
        align-items: center;
        gap: 1rem;
    }

    .play-btn {
        --size: 3rem;
        width: var(--size);
        height: var(--size);
        box-sizing: border-box;
        border: 5px solid #fff;
        border-radius: 50%;
        background-color: transparent;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 0.3rem;
        cursor: pointer;
        
        &:not(.is-playing)::before {
            content: '';
            display: block;
            width: 0;
            height: 0;
            border-style: solid;
            border-width: 10px 0 10px 15px;
            border-color: transparent transparent transparent #fff;
            transform: translateX(0.17rem);
        }

        &.is-playing {

            &::before, &::after {
                content: '';
                display: block;
                background-color: #fff;
                width: 6px;
                height: 20px;
            }

        }
    }

    .time-control {
        flex: 1 1 0;
        height: var(--time-control-height);
        background-color: rgba(255, 255, 255, 0.25);
        border-radius: 9999px;
    }

    .control-bar {
        position: relative;
        height: var(--time-control-height);
        background-color: #FD848B;
        border-radius: 9999px;
    }

    .control-handle {
        --size: calc(var(--time-control-height) * 1.5);
        position: absolute;
        top: calc((var(--size) - var(--time-control-height)) * -0.5);
        right: calc(var(--size) * -0.5);
        width: var(--size);
        height: var(--size);
        border-radius: 50%;
        background-color: #fff;
        cursor: grab;
    }

</style>